import { useContext, useEffect, useState } from "react"
import { submitSurvey } from "../../util/submitSurvey"

import { Data, ExplainersType } from "../../types/dataTypes"
import { Question } from "../../types/questionTypes"
import { authenticityToken } from "../../util/authenticityTokenHelper"
import useMultiStepForm from "../../util/useMultiStepForm"
import SurveyStep from "../SurveyStep"
import Button from "../Button"
import { SurveyContext } from "../Main/SurveyContext"
import moment from "moment-timezone"
import { filterSupportedFieldTypes } from "../../util/filterSupportedFieldTypes"
import { shouldIncludeBranchQuestion } from "../../util/shouldIncludeBranchQuestion"
import SurveyExplainer from "../SurveyExplainer"
import Icon from "../Icon"
import SurveyQuestion from "../SurveyQuestion"
import { toCamelCase } from "~/util/toCamelCase"
import { autofillFields } from "~/util/autoFillFields"
import { excludeHiddenFields } from "~/util/excludeHiddenFields"

export interface SurveyComponentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  name: string
  onSurveySubmit: () => void
  projectPrefix: string
  surveyMetadata: Array<Question>
  surveyEstimates: Record<
    string,
    { name: string; estimatedTimeToComplete: number }
  >
  explainers: ExplainersType
}

export default function SurveyComponent({
  name,
  onSurveySubmit,
  projectPrefix,
  surveyMetadata,
  surveyEstimates,
  explainers,
  ...rest
}: SurveyComponentProps) {
  const [token] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get("token") || ""
  })
  const [submitDisabled, setsubmitDisabled] = useState(false)
  const autofilledData = autofillFields(surveyMetadata)
  const nonHiddenFields = excludeHiddenFields(surveyMetadata)

  const explainerMetaData: Omit<Question, "fieldType"> & {
    fieldType: Question["fieldType"] | "explainer"
  } = {
    branchingLogic: "",
    fieldLabel: surveyEstimates[toCamelCase(name)].name,
    fieldName: name,
    fieldType: "explainer",
    sectionHeader: "",
    fieldNote: "",
    formName: name,
    fieldAnnotation: "",
    textValidationTypeOrShowSliderNumber: "",
    textValidationMax: "",
    textValidationMin: ""
  }

  const [filteredSurvey, setFilteredSurvey] = useState([
    explainerMetaData
  ] as Array<Question>)

  const context = useContext(SurveyContext)

  const [data, setData] = useState({
    // eslint-disable-next-line camelcase
    authenticity_token: authenticityToken(),
    // eslint-disable-next-line camelcase
    project_prefix: projectPrefix,
    token: token,
    // eslint-disable-next-line camelcase
    survey_name: name,
    ...autofilledData
  })

  if (!context) return null

  const updateFields = (newData: Data) => {
    setData((prevData) => ({ ...prevData, ...newData }))
  }

  useEffect(() => {
    const filteredQuestions = filterSupportedFieldTypes(nonHiddenFields)
    setFilteredSurvey((prevFilteredSurvey) =>
      prevFilteredSurvey.concat(filteredQuestions)
    )
  }, [])

  const surveyQuestions = filteredSurvey.filter((question) =>
    shouldIncludeBranchQuestion(data, question)
  )

  const surveySteps: Array<JSX.Element> = surveyQuestions.map(
    (question: Question, index: number) => {
      return (
        <SurveyStep
          key={index}
          data={data}
          question={question}
          updateFields={updateFields}
        />
      )
    }
  )

  const {
    currentStepIndex,
    currentStep,
    isFirstStep,
    previousStep,
    nextStep,
    isLastStep,
    totalSteps
  } = useMultiStepForm(surveySteps)

  const onSubmitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setsubmitDisabled(true)

    if (!isLastStep) {
      nextStep()
      setsubmitDisabled(false)
    } else {
      const autoTimestampQuestion = nonHiddenFields.find(
        (question) => question.fieldAnnotation === "auto_timestamp"
      )
      const timestamp = autoTimestampQuestion
        ? {
            [autoTimestampQuestion.fieldName]: moment()
              .tz("Europe/Copenhagen")
              .format()
          }
        : {}

      setData((prevData) => {
        const dataWithTimestamp = { ...prevData, ...timestamp }
        submitSurvey(dataWithTimestamp)
          .then(() => {
            context?.completeSurvey(name)
            onSurveySubmit()
          })
          .catch(async (error) => {
            if (error?.response?.data?.error === "csrf_token_expired") {
              try {
                // The global axios interceptor fetches a fresh CSRF token
                // automatically before this retry POST
                await submitSurvey(dataWithTimestamp)
                context?.completeSurvey(name)
                onSurveySubmit()
                return
              } catch {
                // Fall through to generic alert
              }
            }
            alert("Something went wrong. Please try again.")
          })
          .finally(() => {
            setsubmitDisabled(false)
          })

        return dataWithTimestamp
      })
    }
  }

  const currentQuestion = surveyQuestions[currentStepIndex]

  return (
    <div {...rest} className="SurveyComponent">
      <form
        method="post"
        onSubmit={onSubmitHandler}
        name="survey"
        className="bg-primary-100 lg:bg-neutral-100 w-full grid min-h-full"
      >
        <input
          type="hidden"
          name="authenticity_token"
          value={authenticityToken()}
        />

        {isFirstStep ? (
          <SurveyExplainer
            explainer={explainers[toCamelCase(currentQuestion.formName)]}
          ></SurveyExplainer>
        ) : (
          <div className="flex flex-col justify-end px-[6px] lg:flex-row pb-14 lg:pb-0 lg:px-0 lg:items-center ">
            <SurveyQuestion
              surveyNumber={context.getSurveyNumber(name)}
              stepNumber={currentStepIndex}
              totalSteps={totalSteps - 1}
              currentQuestion={currentQuestion}
              onPreviousStepClick={previousStep}
            />

            <div className="bg-white p-4 rounded-3xl lg:rounded-none w-full flex justify-center items-center lg:h-full">
              <div className="w-full lg:w-[660px] rounded-3xl lg:p-4 h-fit">
                <div className="text-neutral-700">{currentStep}</div>

                <div className="w-full flex gap-2.5 button-group">
                  <Button
                    type="submit"
                    variant="basic"
                    disabled={submitDisabled}
                    className="w-full ring-neutral-50"
                  >
                    {isLastStep ? (
                      "Indsend besvarelse"
                    ) : (
                      <span className="flex justify-between">
                        Næste spørgsmål
                        <Icon
                          name="arrowRight"
                          className="w-6 h-6 text-inherit"
                        />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}
